import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { API_URL } from '../api';

const OLA_BASE = 'https://api.olamaps.io/places/v1';
const OLA_API_KEY = process.env.REACT_APP_OLA_MAPS_KEY;

const GREEN = '#8ec5fc';
const GREEN_SOFT = '#eef0f1';
const INK = '#1c1c1c';
const MUTED = '#7a7a7a';
const LINE = '#eef0f1';

// How far down the screen the sheet's TOP edge sits — tune this to match
// wherever your header + search bar + category row + ads banner end.
// The sheet always starts here and fills down to the bottom, instead of
// growing/shrinking with content (which was letting it creep up over the ads).
const SHEET_TOP_OFFSET = '38vh';

const PinIcon = ({ size = 18, color = GREEN }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 22s7-6.2 7-12a7 7 0 1 0-14 0c0 5.8 7 12 7 12z" stroke={color} strokeWidth="1.8" strokeLinejoin="round" />
    <circle cx="12" cy="10" r="2.4" stroke={color} strokeWidth="1.8" />
  </svg>
);

const SearchIcon = ({ size = 18, color = MUTED }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="10.5" cy="10.5" r="6.5" stroke={color} strokeWidth="2" />
    <line x1="19.5" y1="19.5" x2="15.2" y2="15.2" stroke={color} strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const CrosshairIcon = ({ size = 20, color = GREEN }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="12" cy="12" r="3" stroke={color} strokeWidth="1.8" />
    <path d="M12 2v3M12 19v3M2 12h3M19 12h3" stroke={color} strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);

const HomeIcon = ({ size = 16, color = GREEN }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M4 11.5 12 4l8 7.5" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M6 10v9h12v-9" stroke={color} strokeWidth="1.8" strokeLinejoin="round" />
  </svg>
);

const WorkIcon = ({ size = 16, color = GREEN }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="3.5" y="7.5" width="17" height="12" rx="1.5" stroke={color} strokeWidth="1.8" />
    <path d="M8.5 7.5V6a2 2 0 0 1 2-2h3a2 2 0 0 1 2 2v1.5" stroke={color} strokeWidth="1.8" />
  </svg>
);

const OtherPinIcon = ({ size = 16, color = GREEN }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 21s6-5.4 6-10.2A6 6 0 0 0 6 10.8C6 15.6 12 21 12 21z" stroke={color} strokeWidth="1.8" strokeLinejoin="round" />
  </svg>
);

const CloseIcon = ({ size = 18, color = INK }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <line x1="5" y1="5" x2="19" y2="19" stroke={color} strokeWidth="2" strokeLinecap="round" />
    <line x1="19" y1="5" x2="5" y2="19" stroke={color} strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const ChevronRight = ({ size = 16, color = MUTED }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <polyline points="9 6 15 12 9 18" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const labelIcon = (label = '') => {
  const l = label.toLowerCase();
  if (l.includes('work') || l.includes('office')) return WorkIcon;
  if (l.includes('home')) return HomeIcon;
  return OtherPinIcon;
};

const formatSavedAddress = (addr) => {
  const parts = [addr.building, addr.street, addr.landmark, addr.city].filter(Boolean);
  const line = parts.join(', ');
  return addr.postal_code ? `${line} - ${addr.postal_code}` : line;
};

export default function LocationModal({ onClose, onSelect, dismissible = true }) {
  const { t } = useTranslation();
  const [detecting, setDetecting] = useState(false);
  const [detectError, setDetectError] = useState('');

  const [searchQuery, setSearchQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState('');

  const [savedAddresses, setSavedAddresses] = useState([]);
  const [savedLoading, setSavedLoading] = useState(false);

  // Guards against out-of-order responses so a slow, stale request can
  // never overwrite results from a newer keystroke.
  const searchAbortRef = useRef(null);
  const requestIdRef = useRef(0);

  useEffect(() => {
    if (!OLA_API_KEY) {
      setSearchError(t('location.apiKeyMissingRestart'));
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Load the user's saved addresses so they show up under the search box,
  // the same way Blinkit surfaces "Saved addresses" beneath location search.
  useEffect(() => {
    const userId = (() => {
      try {
        return localStorage.getItem('phone') || 'guest';
      } catch (_) {
        return 'guest';
      }
    })();
    if (userId === 'guest') return;

    let cancelled = false;
    setSavedLoading(true);
    axios
      .get(`${API_URL}/address/${encodeURIComponent(userId)}`)
      .then((res) => {
        if (cancelled) return;
        if (res.data?.success) setSavedAddresses(res.data.addresses || []);
      })
      .catch((err) => {
        console.error('Failed to fetch saved addresses', err);
      })
      .finally(() => {
        if (!cancelled) setSavedLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  // Search-as-you-type via Ola Maps Autocomplete API
  useEffect(() => {
    const query = searchQuery.trim();

    if (!OLA_API_KEY || query.length <= 2) {
      setSuggestions([]);
      setSearchError(OLA_API_KEY ? '' : searchError);
      setSearching(false);
      return;
    }

    const currentRequestId = ++requestIdRef.current;

    const timeoutId = setTimeout(async () => {
      if (searchAbortRef.current) searchAbortRef.current.abort();
      const controller = new AbortController();
      searchAbortRef.current = controller;

      setSearching(true);
      setSearchError('');

      try {
        const res = await axios.get(`${OLA_BASE}/autocomplete`, {
          params: { input: query, api_key: OLA_API_KEY },
          signal: controller.signal,
        });

        if (currentRequestId !== requestIdRef.current) return; // stale, ignore

        const predictions = res.data?.predictions || [];
        setSuggestions(predictions);
        if (predictions.length === 0) {
          setSearchError(t('location.noMatches'));
        }
      } catch (err) {
        if (axios.isCancel(err) || err.name === 'CanceledError') return; // expected, ignore
        if (currentRequestId !== requestIdRef.current) return;
        console.error('Ola Maps autocomplete error:', err);
        setSearchError(t('location.searchFailed'));
      } finally {
        if (currentRequestId === requestIdRef.current) setSearching(false);
      }
    }, 250); // Ola's autocomplete is built for per-keystroke calls

    return () => clearTimeout(timeoutId);
  }, [searchQuery]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    return () => {
      if (searchAbortRef.current) searchAbortRef.current.abort();
    };
  }, []);

  const handlePlaceSelect = (prediction) => {
    const address = prediction.description || prediction.formatted_address || prediction.name;
    onSelect(address);
    onClose();
  };

  const handleSavedSelect = (addr) => {
    const display = addr.full_address || formatSavedAddress(addr);
    onSelect(display, addr);
    onClose();
  };

  const handleDetect = () => {
    if (!navigator.geolocation) {
      setDetectError(t('location.geoNotSupported'));
      return;
    }
    if (!OLA_API_KEY) {
      setDetectError(t('location.apiKeyMissing'));
      return;
    }

    setDetecting(true);
    setDetectError('');

    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude, longitude } = pos.coords;
        try {
          const res = await axios.get(`${OLA_BASE}/reverse-geocode`, {
            params: { latlng: `${latitude},${longitude}`, api_key: OLA_API_KEY },
          });

          const result = res.data?.results?.[0];
          const address = result?.formatted_address || result?.description;

          if (address) {
            onSelect(address);
            onClose();
          } else {
            setDetectError(t('location.locationFoundNoAddress'));
          }
        } catch (err) {
          console.error('Ola Maps reverse geocode error:', err);
          setDetectError(t('location.reverseGeocodeError'));
        } finally {
          setDetecting(false);
        }
      },
      (err) => {
        setDetecting(false);
        console.error(err);
        if (err.code === err.PERMISSION_DENIED) {
          setDetectError(t('location.permissionDenied'));
        } else if (err.code === err.POSITION_UNAVAILABLE) {
          setDetectError(t('location.positionUnavailable'));
        } else if (err.code === err.TIMEOUT) {
          setDetectError(t('location.timeout'));
        } else {
          setDetectError(t('location.genericLocationError'));
        }
      },
      {
        enableHighAccuracy: true, // use GPS instead of coarse network/IP location
        timeout: 10000,           // fail fast instead of hanging indefinitely
        maximumAge: 0,            // never reuse a stale cached position
      }
    );
  };

  const showingSearchResults = searchQuery.trim().length > 2;

  return (
    <div style={styles.overlay} onClick={dismissible ? onClose : undefined}>
      <style>{`
        @keyframes lm-sheetUp {
          from { transform: translateX(-50%) translateY(100%); }
          to { transform: translateX(-50%) translateY(0); }
        }
        @keyframes lm-fadeIn { from { opacity: 0; } to { opacity: 1; } }
        .lm-sheet { animation: lm-sheetUp 0.28s cubic-bezier(0.22, 1, 0.36, 1) both; }
        .lm-overlay-bg { animation: lm-fadeIn 0.2s ease both; }
        .lm-row:active { background: #f5f5f5; }
      `}</style>

      <div style={styles.sheet} className="lm-sheet" onClick={(e) => e.stopPropagation()}>
        <div style={styles.handle} />

        <div style={styles.headerRow}>
          <h3 style={styles.title}>{t('location.selectLocation')}</h3>
          {dismissible && (
            <button onClick={onClose} style={styles.closeBtn} aria-label={t('location.close')}>
              <CloseIcon />
            </button>
          )}
        </div>

        <div style={styles.body}>
          <div style={styles.searchWrap}>
            <span style={styles.searchIcon}><SearchIcon /></span>
            <input
              type="text"
              placeholder={t('location.searchAreaPlaceholder')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={styles.searchInput}
              autoFocus
            />
          </div>

          {!showingSearchResults && (
            <button onClick={handleDetect} style={styles.detectRow} className="lm-row" disabled={detecting}>
              <span style={styles.detectIconWrap}><CrosshairIcon /></span>
              <span style={styles.detectTextGroup}>
                <span style={styles.detectTitle}>{detecting ? t('location.detectingLocation') : t('location.useCurrentLocation')}</span>
                <span style={styles.detectSubtitle}>{t('location.usingGps')}</span>
              </span>
              {!detecting && <ChevronRight />}
            </button>
          )}
          {detectError && <p style={styles.errorText}>{detectError}</p>}

          {showingSearchResults && (
            <div style={styles.resultsBlock}>
              {searching && <p style={styles.statusText}>{t('location.searching')}</p>}
              {!searching && searchError && <p style={styles.errorText}>{searchError}</p>}
              {suggestions.map((prediction) => (
                <button
                  key={prediction.place_id || prediction.description}
                  onClick={() => handlePlaceSelect(prediction)}
                  style={styles.suggestionRow}
                  className="lm-row"
                >
                  <span style={styles.suggestionIconWrap}><PinIcon size={16} color={MUTED} /></span>
                  <span style={styles.suggestionText}>
                    {prediction.description || prediction.formatted_address || prediction.name}
                  </span>
                </button>
              ))}
            </div>
          )}

          {!showingSearchResults && (
            <div style={styles.savedBlock}>
              <div style={styles.savedHeading}>{t('location.savedAddresses')}</div>

              {savedLoading && <p style={styles.statusText}>{t('location.loadingAddresses')}</p>}

              {!savedLoading && savedAddresses.length === 0 && (
                <p style={styles.emptyText}>{t('location.noSavedAddresses')}</p>
              )}

              {savedAddresses.map((addr) => {
                const Icon = labelIcon(addr.label);
                return (
                  <button
                    key={addr.id}
                    onClick={() => handleSavedSelect(addr)}
                    style={styles.savedRow}
                    className="lm-row"
                  >
                    <span style={styles.savedIconWrap}><Icon /></span>
                    <span style={styles.savedTextGroup}>
                      <span style={styles.savedLabelRow}>
                        <span style={styles.savedLabel}>{addr.label || t('location.other')}</span>
                        {addr.is_default === 1 && <span style={styles.defaultPill}>{t('location.default')}</span>}
                      </span>
                      <span style={styles.savedAddressText}>
                        {addr.full_address || formatSavedAddress(addr)}
                      </span>
                    </span>
                    <ChevronRight />
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const styles = {
  overlay: {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    background: 'rgba(15, 23, 42, 0.5)', zIndex: 1000,
  },
  // Sheet's top edge is pinned to SHEET_TOP_OFFSET (below the ads banner)
  // and it fills down to the bottom of the screen from there. This keeps
  // the top edge fixed no matter how much content (search results, saved
  // addresses) renders inside — it only scrolls internally, it never
  // grows upward over the ads/banner section above it.
  sheet: {
    position: 'absolute', top: "200px", left: '50%', bottom: 0,
    transform: 'translateX(-50%)',
    width: '100%', maxWidth: '480px',
    background: '#fff', borderTopLeftRadius: '20px', borderTopRightRadius: '20px',
    boxShadow: '0 -8px 30px rgba(0,0,0,0.18)',
    display: 'flex', flexDirection: 'column', overflow: 'hidden',
    paddingBottom: 'env(safe-area-inset-bottom, 0px)', boxSizing: 'border-box',
  },
  handle: { width: '38px', height: '4px', borderRadius: '999px', background: '#dcdfe3', margin: '10px auto 0' },
  headerRow: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '14px 18px 10px', borderBottom: `1px solid ${LINE}`,
  },
  title: { margin: 0, fontSize: '16px', fontWeight: 700, color: INK },
  closeBtn: {
    width: '30px', height: '30px', borderRadius: '999px', border: 'none',
    background: '#f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
  },
  // Extra bottom padding so the last saved address always scrolls clear of
  // the floating bottom nav bar instead of hiding underneath it.
  body: { overflowY: 'auto', padding: '14px 18px calc(96px + env(safe-area-inset-bottom, 0px))' },

  searchWrap: {
    display: 'flex', alignItems: 'center', gap: '10px',
    border: `1.5px solid ${LINE}`, borderRadius: '10px', padding: '11px 12px',
    background: '#fafafa',
  },
  searchIcon: { display: 'flex', flexShrink: 0 },
  searchInput: { flex: 1, minWidth: 0, border: 'none', outline: 'none', background: 'transparent', fontSize: '14px', color: INK },

  detectRow: {
    width: '100%', display: 'flex', alignItems: 'center', gap: '12px',
    padding: '14px 4px', marginTop: '4px', border: 'none', borderBottom: `1px solid ${LINE}`,
    background: 'transparent', cursor: 'pointer', textAlign: 'left',
  },
  detectIconWrap: {
    width: '36px', height: '36px', borderRadius: '999px', background: GREEN_SOFT,
    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
  },
  detectTextGroup: { flex: 1, display: 'flex', flexDirection: 'column', gap: '2px', minWidth: 0 },
  detectTitle: { fontSize: '14.5px', fontWeight: 700, color: GREEN },
  detectSubtitle: { fontSize: '12px', color: MUTED },

  errorText: { margin: '8px 0 0 0', fontSize: '13px', color: '#e53935', fontWeight: 600 },
  statusText: { margin: '10px 0', fontSize: '13px', color: MUTED, textAlign: 'center' },
  emptyText: { margin: '4px 0 0 0', fontSize: '13px', color: MUTED },

  resultsBlock: { marginTop: '6px' },
  suggestionRow: {
    width: '100%', display: 'flex', alignItems: 'flex-start', gap: '10px',
    padding: '11px 4px', border: 'none', borderBottom: `1px solid ${LINE}`,
    background: 'transparent', cursor: 'pointer', textAlign: 'left',
  },
  suggestionIconWrap: { marginTop: '2px', flexShrink: 0 },
  suggestionText: { fontSize: '13.5px', color: INK, lineHeight: 1.4 },

  savedBlock: { marginTop: '18px' },
  savedHeading: { fontSize: '13px', fontWeight: 700, color: INK, marginBottom: '4px' },
  savedRow: {
    width: '100%', display: 'flex', alignItems: 'flex-start', gap: '12px',
    padding: '13px 4px', border: 'none', borderBottom: `1px solid ${LINE}`,
    background: 'transparent', cursor: 'pointer', textAlign: 'left',
  },
  savedIconWrap: {
    width: '32px', height: '32px', borderRadius: '999px', background: '#f5f5f5',
    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: '1px',
  },
  savedTextGroup: { flex: 1, display: 'flex', flexDirection: 'column', gap: '3px', minWidth: 0 },
  savedLabelRow: { display: 'flex', alignItems: 'center', gap: '8px' },
  savedLabel: { fontSize: '14px', fontWeight: 700, color: INK },
  defaultPill: {
    fontSize: '10px', fontWeight: 700, color: GREEN, background: GREEN_SOFT,
    padding: '2px 7px', borderRadius: '999px',
  },
  savedAddressText: { fontSize: '12.5px', color: MUTED, lineHeight: 1.4 },
};